import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";

const MARGIN = { top: 30, right: 30, bottom: 70, left: 50 };

type VerticalBarChartProps = {
  height: number;
  data: any;
};

export const VerticalBarChart = ({ height, data }: VerticalBarChartProps) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const axesRef = useRef<SVGGElement>(null);

  const [chartWidth, setChartWidth] = useState<number>(0);

  const boundsWidth = chartWidth - MARGIN.right - MARGIN.left;
  const boundsHeight = height - MARGIN.top - MARGIN.bottom;

  const allNames = data?.map((d: any) => d?.name) || [];
  const max = d3.max(data, (d: any) => d?.count) || 0;

  const xScale = useMemo(() => {
    return d3
      .scaleBand<string>()
      .domain(allNames)
      .range([0, boundsWidth])
      .padding(0.7);
  }, [data, chartWidth, boundsWidth, allNames]);

  const yScale = useMemo(() => {
    return d3
      .scaleLinear()
      .domain([0, max as any])
      .range([boundsHeight, 0]);
  }, [data, height, boundsHeight, max]);

  useEffect(() => {
    const svgElement = d3.select(axesRef.current);
    svgElement.selectAll("*").remove();

    // Draw grid lines
    svgElement
      .selectAll(".h-line")
      .data([0])
      .enter()
      .append("line")
      .attr("class", "grid-line")
      .attr("x1", 0)
      .attr("y1", (d: any) => yScale(d))
      .attr("x2", boundsWidth)
      .attr("y2", (d: any) => yScale(d))
      .style("stroke", "#ccc")
      .style("stroke-width", "1px")
      .style("stroke-dasharray", "4");

    // Draw x-axis
    const xAxisValues = xScale
      .domain()
      .map((d: any) => ({ truncated: d.slice(0, 14), full: d }));

    svgElement
      .append("g")
      .attr("transform", `translate(0,${boundsHeight})`)
      .selectAll(".x-axis-text")
      .data(xAxisValues)
      .enter()
      .append("text")
      .attr("class", "x-axis-text")
      .attr("x", (d: any) => xScale(d.full)! + xScale.bandwidth() / 2) // Center under each bar
      .attr("y", 20) // Position below the x-axis
      .attr("transform", (d: any) => {
        const x = xScale(d.full)! + xScale.bandwidth() / 2;
        const y = 20; // amount below x-axis
        return `rotate(-90, ${x}, ${y})`;
      })
      .text((d: any) => d.truncated) // Use truncated label
      .style("fill", "#000")
      .style("font-size", 12)
      .style("font-weight", 500)
      .style("text-anchor", "end");

    // Draw y-axis
    const yAxisValues = yScale.ticks(3);
    svgElement
      .append("g")
      .attr("transform", `translate(0,0)`)
      .selectAll(".y-axis-text")
      .data(yAxisValues)
      .enter()
      .append("text")
      .attr("class", "y-axis-text")
      .attr("x", -10)
      .attr("y", (d: any) => yScale(d))
      .text((d: any) => d)
      .style("fill", "#000")
      .style("font-size", 12)
      .style("font-weight", 500)
      .style("text-anchor", "end");
  }, [xScale, yScale, boundsHeight, data]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length) {
        const { width } = entries[0].contentRect;
        setChartWidth(width);
      }
    });
    if (chartRef.current) {
      resizeObserver.observe(chartRef.current);
    }

    return () => {
      if (chartRef.current) {
        resizeObserver.unobserve(chartRef.current);
      }
    };
  }, []);

  return (
    <div ref={chartRef} style={{ width: "100%", position: "relative" }}>
      <svg
        width={chartWidth}
        height={height}
        style={{ display: "inline-block", position: "relative" }}
      >
        <g
          width={boundsWidth}
          height={boundsHeight}
          ref={axesRef}
          transform={`translate(${MARGIN.left},${MARGIN.top})`}
        />
        <g
          width={boundsWidth}
          height={boundsHeight}
          transform={`translate(${MARGIN.left},${MARGIN.top})`}
        >
          {data?.map((d: any, i: number) => {
            return (
              <rect
                key={i}
                x={xScale(d?.name)}
                y={yScale(d?.count)}
                height={boundsHeight - yScale(d?.count)}
                width={xScale.bandwidth()}
                fill={d?.color}
              />
            );
          })}
        </g>
      </svg>
    </div>
  );
};
